import { useId, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Search } from 'lucide-react';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import {
  RadioCard,
  RadioDot,
  RadioGroup,
} from '@/components/ui/radio-group.tsx';
import rawBankTransactionsApi from '@/features/dashboard/rawBankTransactions/api/rawBankTransactionsApi.ts';
import { rawBankTransactionsApiQueryKeys } from '@/features/dashboard/rawBankTransactions/api/rawBankTransactionsApi.queryKeys.ts';
import {
  PENDING_TRANSFERS_PAGE_SIZE,
  pendingTransfersFilterCriteria,
} from '@/features/dashboard/rawBankTransactions/api/pendingTransfersQuery.ts';
import { RawTransactionListDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionListDto.ts';
import { RawTransactionStatus } from '@/features/dashboard/rawBankTransactions/types/RawTransactionStatus.ts';
import {
  rawTransactionStatusLabels,
  rawTransactionStatusTones,
} from '@/features/dashboard/rawBankTransactions/types/RawTransactionEnums.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';
import { cn } from '@/lib/utils.ts';
import { useDebounce } from '@/hooks/useDebounce.ts';

interface PendingTransferPickerProps {
  value: string;
  onChange: (id: string) => void;
  error?: string;
}

/**
 * Lista zamiast pola na UUID. Zakres — oczekujące obciążenia — składa
 * `pendingTransfersFilterCriteria`, ta sama definicja tabeli, z której korzysta
 * ekran „Zaimportowane”.
 */
const PendingTransferPicker = ({
  value,
  onChange,
  error,
}: PendingTransferPickerProps) => {
  const labelId = useId();
  const errorId = useId();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selected, setSelected] = useState<RawTransactionListDto | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...rawBankTransactionsApiQueryKeys.raw(),
      'picker',
      debouncedSearchTerm,
    ],
    queryFn: () =>
      rawBankTransactionsApi.getAll(
        { page: 0, size: PENDING_TRANSFERS_PAGE_SIZE },
        pendingTransfersFilterCriteria(debouncedSearchTerm),
      ),
    // Kolejna fraza podmienia listę dopiero, gdy przyjdzie — inaczej lista
    // miga szkieletem po każdym znaku.
    placeholderData: keepPreviousData,
  });

  const transfers = data?.content ?? [];

  /**
   * Wybrany przelew zostaje na wierzchu także wtedy, gdy fraza przestaje go
   * obejmować. Ciche czyszczenie pola byłoby gorsze: użytkownik wraca do
   * wyszukiwarki po drugi wydatek, a przy zapisie okazuje się, że nic nie
   * wybrał.
   */
  const pinned =
    selected && selected.id === value && !transfers.some((t) => t.id === value)
      ? selected
      : null;
  const rows = pinned ? [pinned, ...transfers] : transfers;

  const handleValueChange = (id: string) => {
    setSelected(rows.find((transfer) => transfer.id === id) ?? null);
    onChange(id);
  };

  return (
    <div className="flex flex-col gap-2">
      <InputLabel id={labelId} label="Transakcja z wyciągu" required />

      <div className="flex flex-row items-center gap-2 rounded-md border border-neutral-200 bg-basic-white px-3">
        <Search className="size-4 shrink-0 text-neutral-400" aria-hidden />
        <PrimitiveInput
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Szukaj po kontrahencie lub opisie"
          aria-label="Szukaj przelewu"
          className="border-0 px-0"
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-[62px] rounded-[11px]" />
          ))}
        </div>
      ) : isError ? (
        <LoadError subject="listy przelewów" />
      ) : rows.length === 0 ? (
        <NoItems
          title="Brak nieprzypisanych przelewów"
          description="Zaimportuj wyciąg albo cofnij kwalifikację przelewu, żeby wrócił do oczekujących."
        />
      ) : (
        // Lista przewija się sama, inaczej pięćdziesiąt pozycji spycha
        // wyszukiwarkę i kwotę poza okno.
        <RadioGroup
          value={value}
          onValueChange={handleValueChange}
          aria-labelledby={labelId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="max-h-[290px] overflow-y-auto"
        >
          {rows.map((transfer) => (
            <RadioCard
              key={transfer.id}
              value={transfer.id}
              className={cn(
                // `min-w-0` na karcie: bez niego automatyczne minimum elementu
                // siatki rozpycha kolumnę ponad szerokość listy i kwota ze
                // statusem chowają się za prawą krawędzią.
                'flex min-w-0 flex-row items-center gap-3 border p-3',
                value === transfer.id
                  ? 'border-accent-border bg-accent-100'
                  : 'border-neutral-200 bg-basic-white',
              )}
            >
              <RadioDot />
              <span className="flex min-w-0 flex-1 flex-col gap-[3px] text-left">
                <span className="flex flex-row items-baseline gap-2">
                  <span className="text-xs text-neutral-500">
                    {dayjs(transfer.transactionDate).format('DD.MM.YYYY')}
                  </span>
                  <span className="truncate text-sm font-semibold text-ink">
                    {transfer.counterpartyName || EMPTY_VALUE}
                  </span>
                </span>
                <span className="truncate text-xs leading-4 text-neutral-700">
                  {transfer.description || EMPTY_VALUE}
                </span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1.5">
                <span className="text-sm font-bold tabular-nums text-ink">
                  {formatPrice(
                    transfer.amount.amount,
                    transfer.amount.currency,
                  )}
                </span>
                <StatusDot
                  tone={rawTransactionStatusTones[RawTransactionStatus.PENDING]}
                  label={
                    rawTransactionStatusLabels[RawTransactionStatus.PENDING]
                  }
                  className="text-xs text-neutral-700"
                />
              </span>
            </RadioCard>
          ))}
        </RadioGroup>
      )}

      {error && <InputError id={errorId} error={error} />}
    </div>
  );
};

export default PendingTransferPicker;
